import React from 'react';

import i18n from '@client/utils/i18n';
import { SpectralRanges, SpectrogramPoint } from '@ledium/types';
import { DetailedIntensityInfo, If, WavelengthChart } from '@ledium/ui-common';
import { LightChannelIntensityInput, LightChannelIntensitySlider } from '@ledium/ui-light-channels';
import { Grid, Typography } from '@mui/material';

export interface MixerChannelRanges {
    pfd: SpectralRanges;
    irradiance: SpectralRanges;
}

export interface MixerChannel {
    id: string;
    name: string;
    description?: string;
    channel?: number;
    resolution: number;
    spectrogram: SpectrogramPoint[];
    spectrogramRanges: MixerChannelRanges;
    intensityPercent: number;
    intensity: number;
    intensityRanges: MixerChannelRanges;
    zoneId?: string | null;
    linked?: { projectId?: string; reason: string; spectrumMixName?: string; projectName?: string } | null;
    linkedReason?: string;
    additionalItemListInfo?: string;
}

export interface MixerSettings {
    intensityUnit: 'pfd' | 'irradiance';
    carouselView: boolean;
}

interface SpectrumMixerChannelSettingsItemProps {
    channel: MixerChannel;
    onSliderChange: (channelId: string, value: number) => void;
    onIntensityChange: (channelId: string, value: number) => void;
    formData: Record<string, unknown>;
}

const SpectrumMixerChannelSettingsItem = ({ channel, onSliderChange, onIntensityChange, formData }: SpectrumMixerChannelSettingsItemProps) => {
    const settings = formData.settings as MixerSettings;

    return (
        <Grid container spacing={0} item sx={{ textAlign: 'left' }} display="flex">
            <Grid item xs={8} md={7} style={{ flexGrow: 1 }}>
                <Typography variant="h3">{channel.name}</Typography>
                <Typography variant="body1">{channel.description}</Typography>
                <DetailedIntensityInfo
                    spectrogram={channel.spectrogramRanges}
                    label={i18n('light_channel_details', <b>{channel.channel}</b>, <b>{channel.resolution}</b>)}
                    unit={settings.intensityUnit}
                />
            </Grid>

            <Grid item xs={4} md={5} style={{ flexGrow: 1 }}>
                <WavelengthChart data={channel.spectrogram} unit={settings.intensityUnit} small={true} />
            </Grid>

            <If condition={channel.resolution !== 1}>
                <Grid item container xs={12}>
                    <LightChannelIntensitySlider
                        channel={channel}
                        intensityUnit={settings.intensityUnit}
                        readOnly={!!formData.readOnly}
                        onSliderChange={onSliderChange}
                    />
                </Grid>
            </If>

            <LightChannelIntensityInput channel={channel} readOnly={!!formData.readOnly} onIntensityChange={onIntensityChange} />
        </Grid>
    );
};

export default React.memo(SpectrumMixerChannelSettingsItem);
